/**
 * Platform Health — APHID Status (A7Y-3816).
 *
 * The nightly health check and every APHID observer that watches the platform,
 * one row per workflow file with its recent run outcomes.
 *
 * This table used to be the first of three on `/admin/automation` under the
 * heading "Platform Health". It moved here because a table of APHID observers
 * was never dev automation — that page is the two pipelines — and because a
 * heading reading "Platform Health" inside a nav section called Platform Health
 * said it twice. `showLabel={false}` is what drops the duplicate; the light and
 * the reason stay, since those are the group's state rather than its name.
 *
 * One table per APHID SUBSYSTEM (TPF, 2026-09-09) rather than one for the whole
 * group: sixteen workflow files in a single list said nothing about which part
 * of the observability platform was unhealthy. Which section a file belongs to,
 * and the order the sections render in, are both the server's — this page adds
 * only the `aphid-section-*` test ids its own tests were written against.
 *
 * Each section is a bare table, not a `GroupTable`: a subsystem is a slice of
 * one group, and the group's light — shown once, beside the page title —
 * already covers all of them. Inventing a per-subsystem light would mean
 * deciding a colour the server never computed.
 *
 * It reads the SAME cached `['addis-status']` query the ADDIS Status board
 * populates and issues no request of its own, so the two pages cannot disagree
 * about a light and a click between them renders instantly.
 */
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { api } from '@/lib/api';
import { AddisLightBadge, WorkflowSections } from '@/pages/autobuild/addis-status-parts';

export default function AphidStatusPage() {
  const status = useQuery({
    queryKey: ['addis-status'],
    queryFn: () => api.getAddisStatus(),
    refetchInterval: 60_000,
  });

  const group = status.data?.groups.find((candidate) => candidate.key === 'platform-health');

  return (
    <div className="space-y-4">
      <PageHeader
        title="APHID Status"
        subtitle="Is the platform sound, and would we know if it were not — the nightly health check plus the APHID observers that watch it. One row per workflow, with its recent run outcomes."
      >
        <div className="text-xs text-zinc-500">Polls 60s</div>
      </PageHeader>

      {status.isError ? (
        <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          ADDIS status failed to load. Check the Worker logs for /admin/autobuild/addis-status.
        </div>
      ) : null}

      {!status.data && !status.isError ? (
        <div className="flex items-center gap-2 p-4 text-sm text-zinc-500">
          <RefreshCw className="h-3 w-3 animate-spin" />
          Reading ADDIS status…
        </div>
      ) : null}

      {status.data && !group ? (
        // Named rather than rendered empty: the server decides which groups
        // exist, and one it does not report is a contract mismatch rather than
        // a healthy platform with nothing to say.
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
          ADDIS status reports no platform-health group.
        </div>
      ) : null}

      {group ? (
        <>
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <AddisLightBadge light={group.light} reason={group.reason} />
            <span className="text-xs text-zinc-500">{group.reason}</span>
          </div>
          <WorkflowSections workflows={group.workflows} testIdPrefix="aphid-section" />
        </>
      ) : null}
    </div>
  );
}
